import { useRef, useCallback, useEffect } from 'react';

export interface ExponentialBackoffOptions {
  /** Maximum number of retry attempts (default: 4) */
  maxRetryAttempts?: number;
  /** Jitter range in milliseconds (default: 100ms) */
  jitterMs?: number;
}

/**
 * Calculate retry delay with exponential backoff and jitter
 * @param attemptNumber - The retry attempt number (0 = initial, 1-N = retries)
 * @param jitterMs - Jitter range in milliseconds
 * @returns Delay in milliseconds
 *
 * @example
 * calculateRetryDelay(1, 100) // Returns ~1000ms (1 second ± 100ms jitter)
 * calculateRetryDelay(2, 100) // Returns ~2000ms (2 seconds ± 100ms jitter)
 * calculateRetryDelay(3, 100) // Returns ~4000ms (4 seconds ± 100ms jitter)
 * calculateRetryDelay(4, 100) // Returns ~8000ms (8 seconds ± 100ms jitter)
 */
const calculateRetryDelay = (
  attemptNumber: number,
  jitterMs: number
): number => {
  if (attemptNumber === 0) {
    return 0; // Initial attempt is immediate
  }
  // Exponential backoff: 2^(attempt-1) seconds
  // attemptNumber=1: 2^0 * 1000 = 1000ms (1 second)
  // attemptNumber=2: 2^1 * 1000 = 2000ms (2 seconds)
  // attemptNumber=3: 2^2 * 1000 = 4000ms (4 seconds)
  // attemptNumber=4: 2^3 * 1000 = 8000ms (8 seconds)
  const baseDelayMs = Math.pow(2, attemptNumber - 1) * 1000;
  // Add jitter: random between -jitterMs and +jitterMs
  // Math.random() * 2 - 1 gives range [-1, 1], multiplied by jitterMs
  const jitter = (Math.random() * 2 - 1) * jitterMs;
  // Ensure delay never goes negative (defensive, shouldn't happen with baseDelayMs >= 1000ms)
  return Math.max(0, baseDelayMs + jitter);
};

/**
 * Hook for managing exponential backoff retry logic with jitter
 *
 * @example
 * ```tsx
 * const { scheduleRetry, resetAttempts, hasReachedMaxAttempts } = useExponentialBackoff({
 *   maxRetryAttempts: 4,
 *   jitterMs: 100
 * });
 *
 * // Schedule a retry
 * scheduleRetry(() => {
 *   dispatch(fetchData());
 * });
 *
 * // Reset on success
 * resetAttempts();
 * ```
 */
export const useExponentialBackoff = (
  options: ExponentialBackoffOptions = {}
) => {
  const { maxRetryAttempts = 4, jitterMs = 100 } = options;

  const retryAttemptRef = useRef<number>(0);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Schedule a retry with exponential backoff
   * @param retryFn - Function to execute on retry
   * @returns true if retry was scheduled, false if max attempts reached
   */
  const scheduleRetry = useCallback(
    (retryFn: () => void): boolean => {
      // Check if we've exceeded max retry attempts
      if (retryAttemptRef.current >= maxRetryAttempts) {
        return false;
      }

      // Increment attempt counter and calculate delay
      retryAttemptRef.current += 1;
      const delay = calculateRetryDelay(retryAttemptRef.current, jitterMs);

      // Clear any existing timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      // Schedule the retry
      retryTimeoutRef.current = setTimeout(() => {
        retryFn();
      }, delay);

      return true;
    },
    [maxRetryAttempts, jitterMs]
  );

  /**
   * Reset the retry attempt counter
   */
  const resetAttempts = useCallback(() => {
    retryAttemptRef.current = 0;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  /**
   * Clear any pending retry timeout
   */
  const clearPendingRetry = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  /**
   * Check if max retry attempts have been reached
   */
  const hasReachedMaxAttempts = useCallback((): boolean => {
    return retryAttemptRef.current >= maxRetryAttempts;
  }, [maxRetryAttempts]);

  /**
   * Get the current retry attempt number
   */
  const getCurrentAttempt = useCallback((): number => {
    return retryAttemptRef.current;
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  return {
    scheduleRetry,
    resetAttempts,
    clearPendingRetry,
    hasReachedMaxAttempts,
    getCurrentAttempt,
  };
};
